/**
 * Pure UI ↔ API converters for a quote line item.
 *
 * Extracted from QuoteForm so the price_unset regression suite can
 * import them without pulling the React tree.
 *
 * A5/A6 contract:
 *   - `toUiItem`: a saved item with `unit_price_minor == null` renders
 *     the price input BLANK (never "0.00") and carries `price_unset=true`.
 *   - `toApiItem`: whatever the user has typed is what we send. A stale
 *     `price_unset=true` from an AI draft must NOT override a real
 *     numeric input. Only truly blank/whitespace inputs get nulled so
 *     the backend PDF gate can trip.
 *   - `applyItemPatch`: user typing a valid number in the price field
 *     clears the AI's `price_unset=true` flag. Blank / whitespace /
 *     garbage does NOT clear the flag — the "Fiyat girin" badge stays.
 */
import { majorToMinor, minorToMajor } from '../../lib/quoteMoney';

export function isPriceMissing(it = {}) {
  if (it.price_unset === true) return true;
  if (Object.prototype.hasOwnProperty.call(it, 'unit_price_major')) {
    const raw = String(it.unit_price_major ?? '').trim().replace(',', '.');
    return raw === '' || !Number.isFinite(Number(raw)) || Number(raw) <= 0;
  }
  return it.unit_price_minor == null || Number(it.unit_price_minor) <= 0;
}

export function toUiItem(it = {}) {
  const priceUnset = isPriceMissing(it);
  return {
    catalog_item_id: it.catalog_item_id || null,
    master_family_id: it.master_family_id || null,
    master_variant_id: it.master_variant_id || null,
    master_catalog_revision: it.master_catalog_revision || null,
    technical_attributes: it.technical_attributes || {},
    item_type: it.item_type || 'product',
    name: it.name || '',
    description: it.description || '',
    unit: it.unit || '',
    quantity: it.quantity_unset ? '' : (it.quantity ?? 1),
    quantity_unset: !!it.quantity_unset,
    design_note: it.design_note || '',
    unit_price_major: priceUnset ? '' : minorToMajor(it.unit_price_minor || 0).toString(),
    cost_price_major: minorToMajor(it.cost_price_minor || 0).toString(),
    line_discount_percent: Number(it.line_discount_percent || 0),
    price_unset: priceUnset,
  };
}

export function toApiItem(it) {
  const raw = String(it.unit_price_major ?? '').trim();
  // A non-empty user input ALWAYS wins over a stale AI `price_unset`
  // flag. Only when the field is truly blank do we forward null so the
  // backend PDF gate (400 reason=missing_prices) can trip.
  const priceUnset = raw === '';
  return {
    catalog_item_id: it.catalog_item_id || null,
    master_family_id: it.master_family_id || null,
    master_variant_id: it.master_variant_id || null,
    master_catalog_revision: it.master_catalog_revision || null,
    technical_attributes: it.technical_attributes || {},
    item_type: it.item_type,
    name: it.name,
    description: it.description,
    unit: it.unit,
    quantity: it.quantity_unset
      ? 0
      : (Number(String(it.quantity || 0).replace(',', '.')) || 0),
    quantity_unset: !!it.quantity_unset,
    design_note: it.design_note || null,
    unit_price_minor: priceUnset ? null : majorToMinor(raw),
    cost_price_minor: majorToMinor(it.cost_price_major || 0),
    line_discount_percent: Number(it.line_discount_percent || 0),
  };
}

export function applyItemPatch(currentItem, patch) {
  let normalizedPatch = patch;
  if (Object.prototype.hasOwnProperty.call(patch, 'quantity')) {
    const rawQty = String(patch.quantity ?? '').trim().replace(',', '.');
    if (rawQty !== '' && Number.isFinite(Number(rawQty)) && Number(rawQty) >= 0) {
      normalizedPatch = { ...normalizedPatch, quantity_unset: false, design_note: '' };
    }
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'unit_price_major')) {
    const raw = String(patch.unit_price_major ?? '').trim();
    if (raw !== '' && Number.isFinite(Number(raw.replace(',', '.')))) {
      normalizedPatch = { ...patch, price_unset: false };
    }
  }
  return { ...currentItem, ...normalizedPatch };
}
